import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    TextInput,
    DateInput,
    NumberInput,
    Text,
    Theme,
    Button,
    Modal,
    Services
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import Grid from "@mui/material/Grid";
import { approveClaimSingleItem, getClaim, declineClaimSingleItem, referClaimSingleItem } from '../../../actions/adjudication.js';
import PermissionsComponent from '../../../components/PermissionsComponent.js';
import permissionLevel from '../../../services/permissions.js';

class SingleItemForm extends PureComponent {
    state = {
        notes: this.props.defaults.notes instanceof Array ? this.props.defaults.notes.join('\n') : this.props.defaults.notes || null,
        amountSubmitted: this.props.defaults.amountSubmitted || null,
        amountPaid: this.props.defaults.amountPaid || null,
        adjudicatedAmount: this.props.defaults.adjudicatedAmount || null,
        category: this.props.defaults.category || null,
        service: this.props.defaults.service || null,
        serviceDate: this.props.defaults.serviceDate ? dates.getUTC(this.props.defaults.serviceDate, 'YYYY/MM/DD') : null,
        provider: this.props.defaults.provider.name || null,
        deductible: this.props.defaults.deductible || null,
        reimbursement: this.props.defaults.reimbursement || null,
        hsaAmount: this.props.defaults.hsaAmount || null,
        description: this.props.defaults.description || null,
        amountSubmittedToFirstPayor: this.props.defaults.amountSubmittedToFirstPayor || null,
        totalPaidByFirstPayor: this.props.defaults.totalPaidByFirstPayor || null,
        amountNotCoveredByFirstPayor: this.props.defaults.amountNotCoveredByFirstPayor || null,
        showConfirmModal: false,
        action: ''
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Adjudicate Claim', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    componentDidMount() {
        const category = this.props.defaults.category || 'Paramedicals';

        this.setState({
            serviceOptions: this.props.claimCategories[category].reverse()
        });
    }

    _handleChange = (type, value) => {
        this.setState({ [type]: value });
    };

    _toggleConfirmModal = async (action = null) => {
        if (!this.state.showConfirmModal) {
            const isValid = await this._validateForm();

            if (!isValid) {
                return false;
            }
        }

        this.setState({
            showConfirmModal: !this.state.showConfirmModal,
            action
        });

        return true;
    };

    _getData = () => {
        // set the correct hsaAmount, on when it's a standard claim, can the value get changed.
        let hsaAmount = Number(this.state.hsaAmount);
        const isHsaClaim = this.props.defaults.type.toLowerCase() === 'hsa';
        const isLsaClaim = this.props.defaults.type.toLowerCase() === 'lsa';

        if (isHsaClaim || isLsaClaim || !this.props.showHsaConversionAndTopOff) {
            hsaAmount = 0;
        }

        return {
            notes: !this.state.notes || this.state.notes.length === 0 ? null : this.state.notes,
            claimFormat: this.props.claimFormat,
            claimType: this.props.claimType,
            amountSubmitted: Number(this.state.amountSubmitted),
            amountPaid: Number(this.state.amountPaid),
            adjudicatedAmount: Number(this.state.adjudicatedAmount),
            category: this.state.category,
            service: this.state.service,
            serviceDate: dates.getUTC(this.state.serviceDate),
            provider: this.state.provider,
            deductible: Number(this.state.deductible),
            reimbursement: Number(this.state.reimbursement),
            hsaAmount: hsaAmount,
            description: this.state.description,
            amountSubmittedToFirstPayor: Number(this.state.amountSubmittedToFirstPayor),
            totalPaidByFirstPayor: Number(this.state.totalPaidByFirstPayor),
            amountNotCoveredByFirstPayor: Number(this.state.amountNotCoveredByFirstPayor)
        };
    };

    _decline = async () => {
        this.props?.onAdjudicationStart();
        this.setState({ showConfirmModal: false });

        const success = await this.props.dispatch(declineClaimSingleItem(this.props.claimID, this._getData()));

        if (!success) {
            return false;
        }

        await this.props.dispatch(getClaim(this.props.claimID));

        return true;
    };

    _refer = async () => {
        this.props?.onAdjudicationStart();
        this.setState({ showConfirmModal: false });

        const success = await this.props.dispatch(referClaimSingleItem(this.props.claimID, this._getData()));

        if (!success) {
            return false;
        }

        await this.props.dispatch(getClaim(this.props.claimID));

        return true;
    };

    _approve = async () => {
        this.props?.onAdjudicationStart();
        this.setState({ showConfirmModal: false });

        const success = await this.props.dispatch(approveClaimSingleItem(this.props.claimID, this._getData()));

        if (!success) {
            return false;
        }

        await this.props.dispatch(getClaim(this.props.claimID));

        return true;
    };

    _validateForm = async () => {
        const amountSubmittedError = await Services.validateInput('checkEmpty', this.state.amountSubmitted || '');
        const adjudicatedAmountError = await Services.validateInput('checkEmpty', this.state.adjudicatedAmount || '');
        const amountPaidError = await Services.validateInput('checkEmpty', this.state.amountPaid || '');
        const categoryError = await Services.validateInput('checkEmpty', this.state.category || '');
        const serviceError = await Services.validateInput('checkEmpty', this.state.service || '');
        const serviceDateError = await Services.validateInput('date', this.state.serviceDate);
        const providerError = await Services.validateInput('checkEmpty', this.state.provider || '');
        const deductibleError = await Services.validateInput('checkEmpty', this.state.deductible || '');
        const reimbursementError = await Services.validateInput('checkEmpty', this.state.reimbursement || '');
        const hsaAmountError = this.props.claimType.toLowerCase() === 'standard' && this.props.showHsaConversionAndTopOff ? // do not attempt to validate if the hsa field is hidden
            await Services.validateInput('checkEmpty', this.state.hsaAmount || '')
            :
            null;

        let descriptionError = false;
        let amountSubmittedToFirstPayorError = false;
        let totalPaidByFirstPayorError = false;
        let amountNotCoveredByFirstPayorError = false;

        if (this.props.claimFormat === 'cob') {
            // check the fields that are required for cob
            descriptionError = await Services.validateInput('checkEmpty', this.state.description || '');
            amountSubmittedToFirstPayorError = await Services.validateInput('checkEmpty', this.state.amountSubmittedToFirstPayor || '');
            totalPaidByFirstPayorError = await Services.validateInput('checkEmpty', this.state.totalPaidByFirstPayor || '');
            amountNotCoveredByFirstPayorError = await Services.validateInput('checkEmpty', this.state.amountNotCoveredByFirstPayor || '');
        }

        this.setState({
            amountSubmittedError,
            adjudicatedAmountError,
            amountPaidError,
            serviceError,
            categoryError,
            serviceDateError,
            providerError,
            hsaAmountError,
            deductibleError,
            reimbursementError,
            descriptionError,
            amountSubmittedToFirstPayorError,
            totalPaidByFirstPayorError,
            amountNotCoveredByFirstPayorError
        });

        if (
            amountSubmittedError ||
            adjudicatedAmountError ||
            amountPaidError ||
            serviceError ||
            serviceError ||
            serviceDateError ||
            providerError ||
            hsaAmountError ||
            deductibleError ||
            reimbursementError ||
            descriptionError ||
            amountSubmittedToFirstPayorError ||
            totalPaidByFirstPayorError ||
            amountNotCoveredByFirstPayorError
        ) {
            return false;
        }

        return true;
    };

    _selectCategory = async (event) => {
        await this.setState({
            category: event.target.value
        });

        this._setServiceOptions(event.target.value);
    };

    _selectService = async (event) => {
        await this.setState({
            service: event.target.value
        });
    };

    _setServiceOptions = (category) => {
        const options = this.props.claimCategories[category].reverse();

        this.setState({
            serviceOptions: options,
            service: options[0]
        });
    };

    render() {
        const styles = {
            btnBlock: {
                marginTop: 20,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center'
            }
        };

        let categoryOptions = [
            'Drugs',
            'Dental',
            'Health',
            'Paramedicals',
            'Vision',
            'Lifestyle Spending',
            'Cost Plus'
        ];

        const claimType = this.props.defaults.type.toUpperCase();

        if (claimType === 'HSA') {
            categoryOptions.push('Dental');
        }

        return (
            <div>
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    spacing={2}
                >
                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Amount Submitted"
                            fullWidth
                            prefix="$"
                            value={ this.state.amountSubmitted || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("amountSubmitted", e.target.value)}
                            error={ this.state.amountSubmittedError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Adjudicated Amount"
                            fullWidth
                            prefix="$"
                            value={ this.state.adjudicatedAmount || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("adjudicatedAmount", e.target.value)}
                            error={ this.state.adjudicatedAmountError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Amount Paid"
                            fullWidth
                            prefix="$"
                            value={ this.state.amountPaid || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("amountPaid", e.target.value)}
                            error={ this.state.amountPaidError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Category"
                            fullWidth
                            value={ this.state.category || '' }
                            onChange={this._selectCategory}
                            variant="outlined"
                            error={ this.state.categoryError }
                            select
                            required
                            selectOptions={ categoryOptions }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Service"
                            variant="outlined"
                            value={this.state.service || ''}
                            onChange={this._selectService}
                            select
                            required
                            fullWidth
                            selectOptions={ this.state.serviceOptions }
                            error={ this.state.serviceError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <DateInput
                            label="Service Date"
                            fullWidth
                            value={ this.state.serviceDate || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("serviceDate", e.target.value)}
                            error={ this.state.serviceDateError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Coinsurance"
                            fullWidth
                            suffix="%"
                            value={ this.state.reimbursement || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("reimbursement", e.target.value)}
                            error={ this.state.reimbursementError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Provider"
                            fullWidth
                            value={ this.state.provider || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("provider", e.target.value)}
                            error={ this.state.providerError }
                        />
                    </Grid>

                    {
                        this.props.claimType === 'standard' && this.props.showHsaConversionAndTopOff &&
                            <Grid item xs={12} sm={4}>
                                <NumberInput
                                    label="HSA Used"
                                    fullWidth
                                    prefix="$"
                                    value={ this.state.hsaAmount || '' }
                                    variant="outlined"
                                    onChange={(e) => this._handleChange("hsaAmount", e.target.value)}
                                    error={ this.state.hsaAmountError }
                                />
                            </Grid>
                    }

                    <Grid item xs={12} sm={4}>
                        <NumberInput
                            label="Deductible"
                            fullWidth
                            prefix="$"
                            value={ this.state.deductible || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("deductible", e.target.value)}
                            error={ this.state.deductibleError }
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextInput
                            label="Description"
                            fullWidth
                            value={ this.state.description || '' }
                            variant="outlined"
                            onChange={(e) => this._handleChange("description", e.target.value)}
                            error={ this.state.descriptionError }
                        />
                    </Grid>

                    {
                        (this.props.claimFormat === 'cob') &&
                            <React.Fragment>
                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Amount Submitted to First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ this.state.amountSubmittedToFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("amountSubmittedToFirstPayor", e.target.value)}
                                        error={ this.state.amountSubmittedToFirstPayorError }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Total Paid by First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ this.state.totalPaidByFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("totalPaidByFirstPayor", e.target.value)}
                                        error={ this.state.totalPaidByFirstPayorError }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <NumberInput
                                        label="Amount Not Covered by First Payor"
                                        fullWidth
                                        prefix="$"
                                        value={ this.state.amountNotCoveredByFirstPayor || '' }
                                        variant="outlined"
                                        onChange={(e) => this._handleChange("amountNotCoveredByFirstPayor", e.target.value)}
                                        error={ this.state.amountNotCoveredByFirstPayorError }
                                    />
                                </Grid>
                            </React.Fragment>
                    }
                </Grid>

                <Text
                    mediumLarge
                    display="block"
                    margin="20px 0 0 0"
                    color={ Theme.colors.skyBlue }
                >
                    Notes
                </Text>

                <TextInput
                    fullWidth
                    value={ this.state.notes || '' }
                    variant="outlined"
                    onChange={(e) => this._handleChange("notes", e.target.value)}
                    multiline={ true }
                    rows={ 5 }
                />

                { 
                    this.props.claimsBlocked
                        ? (
                            <Text
                                medium
                                display="block"
                                margin="20px 0 0 0"
                                color="red"
                                bold
                            >
                    This member’s claims are currently blocked. This includes claim adjudication and claims submissions from all users and sources.
                            </Text>
                        )
                        : null
                }

                <PermissionsComponent
                    permission="Adjudicate Claim"
                    accessType={this.props.accessType}
                    level="write"
                >
                    <div style={ styles.btnBlock }>
                        <Button
                            onClick={ () => {
                                this._toggleConfirmModal('decline');
                            }}
                            transparent
                            customStyle={{ marginRight: 1.25 }}
                            disabled={this.props.claimsBlocked}
                        >
                            Decline
                        </Button>

                        {
                            (this.state.category.toUpperCase() !== 'DENTAL' || claimType !== 'STANDARD') &&
                                <>
                                    <Button
                                        onClick={ () => {
                                            this._toggleConfirmModal('refer');
                                        }}
                                        transparent
                                        customStyle={{ marginRight: 1.25 }}
                                        disabled={this.props.claimsBlocked}
                                    >
                                        Refer
                                    </Button>

                                    <Button
                                        onClick={ () => {
                                            this._toggleConfirmModal('approve');
                                        }}
                                        color="primary"
                                        disabled={this.props.claimsBlocked}
                                    >
                                        Approve
                                    </Button>
                                </>
                        }
                    </div>
                </PermissionsComponent>

                <Modal
                    open={this.state.showConfirmModal}
                    onClose={ this._toggleConfirmModal }
                    title="Are you sure?"
                    primaryButton={{
                        label: "Continue",
                        onClick: () => {
                            if (this.state.action === 'decline') {
                                return this._decline();
                            }
    
                            if (this.state.action === 'refer') {
                                return this._refer();
                            }
    
                            return this._approve();
                        }
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        { `Are you sure you want to ${ this.state.action } this claim? This action is final and cannot be undone.` }
                    </Text>
                </Modal>
            </div>
        );
    }
}

SingleItemForm.propTypes = {
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    accessType: state.master.access
});

export default connect(mapStateToProps)(SingleItemForm);